<template>
  <q-page class="q-pa-md bg-grey-1">
    <div class="row q-col-gutter-xl">
      <!-- LEFT: Search + list -->
      <div class="col-12 col-md-5">
        <q-card flat bordered class="q-pa-md bg-white rounded-borders shadow-2">
          <div class="row items-center">
            <q-icon name="search" class="text-primary q-mr-sm" size="md" />
            <div class="text-h6 text-primary">Find Employee</div>
          </div>

          <q-input
            v-model="search"
            outlined
            dense
            class="q-mt-md"
            placeholder="Search by name or employee ID..."
            debounce="300"
            @update:model-value="fetchEmployees"
            clearable
          >
            <template #prepend><q-icon name="search" /></template>
          </q-input>

          <q-scroll-area class="q-mt-md" style="height: 70vh">
            <q-list bordered separator>
              <q-item
                v-for="emp in employees"
                :key="emp.id"
                clickable
                :active="selected?.id === emp.id"
                active-class="bg-blue-1"
                class="q-pa-sm"
                @click="selectEmployee(emp)"
              >
                <q-item-section avatar>
                  <q-avatar size="42px">
                    <img
                      :src="emp.avatar || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(emp.name)"
                    />
                  </q-avatar>
                </q-item-section>

                <q-item-section>
                  <q-item-label class="text-weight-medium">
                    {{ emp.name }}
                  </q-item-label>
                  <q-item-label caption>
                    {{ emp.position }} — {{ emp.department }}
                  </q-item-label>
                </q-item-section>

                <q-item-section side>
                  <q-btn
                    color="primary"
                    dense
                    outline
                    label="View"
                    @click.stop="openProfilePdf(emp)"
                  />
                </q-item-section>
              </q-item>

              <q-item v-if="!employees.length">
                <q-item-section class="text-grey">
                  No results yet. Try searching.
                </q-item-section>
              </q-item>
            </q-list>
          </q-scroll-area>
        </q-card>
      </div>

      <!-- RIGHT: Details + Generate -->
      <div class="col-12 col-md-7" v-if="selected">
        <q-card flat bordered class="q-pa-lg bg-white rounded-borders shadow-2">
          <div class="text-h6 text-primary">
            Generate Certificate for {{ selected.name }}
          </div>

          <q-card flat bordered class="q-pa-md q-mt-md">
            <div class="row q-col-gutter-lg">
              <div class="col-12 col-sm-6">
                <div class="text-bold">Position</div>
                <div>{{ selected.position }}</div>
              </div>
              <div class="col-12 col-sm-6">
                <div class="text-bold">Department</div>
                <div>{{ selected.department }}</div>
              </div>
              <div class="col-12 col-sm-6 q-mt-sm">
                <div class="text-bold">Start Date</div>
                <div>{{ formatDate(selected.start_date) }}</div>
              </div>
            </div>

            <q-separator spaced />

            <div class="q-mt-sm">
              <div class="text-bold q-mb-xs">Job Description</div>
              <div class="text-body2">
                {{ selected.job_description || '—' }}
              </div>
            </div>
          </q-card>

          <!-- LANGUAGE -->
          <div class="q-mt-md">
            <div class="text-bold q-mb-xs">Document Language</div>
            <q-select
              v-model="language"
              :options="languageOptions"
              outlined
              dense
              emit-value
              map-options
              label="Select language"
            />
          </div>

          <!-- GERMAN ARBEITSZEUGNIS RATINGS -->
          <div class="q-mt-md">
            <div class="text-bold q-mb-xs">Performance Ratings (German Structure)</div>

            <div class="row q-col-gutter-md">
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.work_quality"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Work Quality"
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.work_style"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Work Style"
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.commitment"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Commitment"
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.professional_knowledge"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Professional Knowledge"
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.stress_resistance"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Stress Resistance"
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.social_behavior"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Social Behavior"
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.problem_solving"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Problem-Solving / Analytical Skills"
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.learning_ability"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Learning Ability"
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.decision_making"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Decision-Making"
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.communication"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Communication Skills"
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.teamwork"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Teamwork"
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.integrity"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Integrity / Responsibility"
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.leadership_evaluation"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Leadership"
                  clearable
                />
              </div>
              <div class="col-12 col-sm-6">
                <q-select
                  v-model="ratings.overall_grade"
                  :options="ratingOptions"
                  outlined
                  dense
                  emit-value
                  map-options
                  label="Overall Grade"
                />
              </div>
            </div>
          </div>

          <div class="q-mt-lg flex justify-end">
            <q-btn
              color="primary"
              :loading="loading"
              label="Generate Document"
              icon="description"
              @click="generateDocument"
            />
          </div>
        </q-card>
      </div>
    </div>

    <!-- PDF Dialog -->
    <q-dialog v-model="showPdf" maximized>
      <q-card class="column fit">
        <q-bar class="bg-primary text-white">
          <div class="text-subtitle2">{{ currentPdfTitle }}</div>
          <q-space />
          <q-btn dense flat icon="close" v-close-popup />
        </q-bar>
        <q-card-section class="col">
          <iframe
            v-if="pdfUrl"
            :src="pdfUrl"
            width="100%"
            height="100%"
            style="border: 0"
          />
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useQuasar } from 'quasar'
import { api } from '../api'

const $q = useQuasar()

const search = ref('')
const employees = ref([])
const selected = ref(null)
const loading = ref(false)

const language = ref('German')
const languageOptions = [
  { label: 'German (Deutsch)', value: 'German' },
  { label: 'English', value: 'English' },
]

const ratingOptions = [
  { label: '1 - Sehr gut / Very Good', value: 1 },
  { label: '2 - Gut / Good', value: 2 },
  { label: '3 - Befriedigend / Satisfactory', value: 3 },
  { label: '4 - Ausreichend / Adequate', value: 4 },
  { label: '5 - Mangelhaft / Poor', value: 5 },
]

const ratings = ref({
  work_quality: null,
  work_style: null,
  commitment: null,
  professional_knowledge: null,
  stress_resistance: null,
  social_behavior: null,
  problem_solving: null,
  learning_ability: null,
  decision_making: null,
  communication: null,
  teamwork: null,
  integrity: null,
  leadership_evaluation: null,
  overall_grade: null,
})

const showPdf = ref(false)
const pdfUrl = ref('')
const currentPdfTitle = ref('')
const currentBlobUrl = ref(null)

function revokeCurrentBlob () {
  if (currentBlobUrl.value) {
    URL.revokeObjectURL(currentBlobUrl.value)
    currentBlobUrl.value = null
  }
}
onBeforeUnmount(revokeCurrentBlob)

function formatDate (s) {
  if (!s) return '-'
  return new Date(s).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function normalizeRating (val, fallback = 1) {
  if (val === null || val === undefined || val === '') return fallback
  const n = Math.round(Number(val))
  if (Number.isNaN(n)) return fallback
  return Math.min(5, Math.max(1, n))
}

async function fetchEmployees () {
  const { data } = await api.get('employees/', {
    params: { q: search.value },
  })
  employees.value = data
}

function selectEmployee (emp) {
  selected.value = emp
  ratings.value = {
    work_quality: normalizeRating(emp.work_quality),
    work_style: normalizeRating(emp.work_style),
    commitment: normalizeRating(emp.commitment),
    professional_knowledge: normalizeRating(emp.professional_knowledge),
    stress_resistance: normalizeRating(emp.stress_resistance),
    social_behavior: normalizeRating(emp.social_behavior),
    problem_solving: normalizeRating(emp.problem_solving),
    learning_ability: normalizeRating(emp.learning_ability),
    decision_making: normalizeRating(emp.decision_making),
    communication: normalizeRating(emp.communication),
    teamwork: normalizeRating(emp.teamwork),
    integrity: normalizeRating(emp.integrity),
    leadership_evaluation: emp.leadership_evaluation ? normalizeRating(emp.leadership_evaluation) : null,
    overall_grade: normalizeRating(emp.overall_grade || emp.rating),
  }
}

async function openProfilePdf (emp) {
  revokeCurrentBlob()
  const resp = await api.get(`employees/${emp.id}/profile.pdf`, {
    responseType: 'blob',
  })
  const blob = new Blob([resp.data], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  currentBlobUrl.value = url
  pdfUrl.value = url
  showPdf.value = true
}

async function generateDocument () {
  if (!selected.value) return
  loading.value = true
  revokeCurrentBlob()

  try {
    const resp = await api.post(
      `employees/${selected.value.id}/recommendation.pdf`,
      {
        language: language.value,
        work_quality: ratings.value.work_quality,
        work_style: ratings.value.work_style,
        commitment: ratings.value.commitment,
        professional_knowledge: ratings.value.professional_knowledge,
        stress_resistance: ratings.value.stress_resistance,
        social_behavior: ratings.value.social_behavior,
        problem_solving: ratings.value.problem_solving,
        learning_ability: ratings.value.learning_ability,
        decision_making: ratings.value.decision_making,
        communication: ratings.value.communication,
        teamwork: ratings.value.teamwork,
        integrity: ratings.value.integrity,
        leadership_evaluation: ratings.value.leadership_evaluation,
        overall_grade: ratings.value.overall_grade,
      },
      { responseType: 'blob' }
    )

    const blob = new Blob([resp.data], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    currentBlobUrl.value = url
    pdfUrl.value = url
    showPdf.value = true
  } catch (e) {
    $q.notify({ type: 'negative', message: 'Failed to generate document' })
  } finally {
    loading.value = false
  }
}

onMounted(fetchEmployees)
</script>

<style scoped>
.rounded-borders {
  border-radius: 16px;
}
iframe {
  background: #eee;
}
</style>
